import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  ArrowUpRight,
  Browsers,
  ChatText,
  Check,
  Globe,
  MagnifyingGlass,
  RocketLaunch,
  SquaresFour,
  UserSquare,
  Wrench
} from '@phosphor-icons/react';
import { PROJECTS, SERVICES } from '../data/site';
import Container from '../components/ui/Container';
import Chip from '../components/ui/Chip';
import IconBadge from '../components/ui/IconBadge';
import Reveal, { EASE, staggerChild, staggerParent } from '../components/ui/Reveal';
import PageHeader from '../components/PageHeader';
import CtaBand from '../components/CtaBand';

const SERVICE_ICONS = {
  website: Browsers,
  system: SquaresFour,
  portfolio: UserSquare
};

const EXTRAS = [
  { icon: Globe, title: '도메인 연결', description: '새 도메인 구매부터 기존 도메인 연결까지' },
  { icon: RocketLaunch, title: '호스팅과 배포', description: '서버 세팅과 배포, 주소 연결까지 처리' },
  { icon: ChatText, title: '문의 폼과 알림', description: '방문자 문의가 이메일로 바로 도착' },
  { icon: MagnifyingGlass, title: '검색 노출 설정', description: '제목, 설명, 공유 미리보기 기본 세팅' },
  { icon: Wrench, title: '운영 중 수정', description: '배포 후 문구, 이미지, 기능 수정 요청' }
];

function ServiceVisual({ service }) {
  const project = PROJECTS.find((item) => item.slug === service.project);
  const Icon = SERVICE_ICONS[service.id];

  if (project) {
    return (
      <a
        href={project.url}
        target="_blank"
        rel="noreferrer"
        className="group block"
        aria-label={`${project.name} 사이트 열기`}
      >
        <div className="bg-brand-gradient relative rounded-4xl p-4 sm:p-6">
          <div className="overflow-hidden rounded-2xl bg-white p-1.5 shadow-frame">
            <img
              src={project.thumb}
              alt={project.alt}
              width={900}
              height={562}
              className="w-full rounded-xl transition-transform duration-700 ease-out group-hover:scale-[1.02]"
            />
          </div>
        </div>
        <p className="mt-3 inline-flex items-center gap-1 text-[14px] text-ink-muted">
          실제 사례: {project.name}
          <ArrowUpRight size={14} weight="bold" />
        </p>
      </a>
    );
  }

  return (
    <div className="bg-night-glow relative flex aspect-[4/3] flex-col justify-between overflow-hidden rounded-4xl bg-night p-7 text-white sm:p-9">
      <img
        src="/main_logo.png"
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-12 -right-8 w-[260px] rotate-[-12deg] opacity-25"
      />
      <IconBadge icon={Icon} tone="inverse" size="lg" />
      <div className="relative">
        <p className="text-2xl font-bold tracking-tight sm:text-3xl">
          작업물이 먼저 보이고,
          <br />
          연락처가 바로 이어지는 구성
        </p>
        <p className="mt-3 max-w-sm text-[15px] leading-[1.7] text-white/75">
          첫 화면에서 무엇을 하는 사람인지, 어떤 작업을 했는지 바로 읽히도록 순서를 잡습니다.
        </p>
      </div>
    </div>
  );
}

function ServicesPage() {
  const reduce = useReducedMotion();
  const [activeId, setActiveId] = useState(SERVICES[0].id);
  const active = SERVICES.find((service) => service.id === activeId);
  const ActiveIcon = SERVICE_ICONS[active.id];

  return (
    <>
      <PageHeader
        title="필요한 범위만큼 맡길 수 있습니다"
        description="기능 하나만 추가해도 되고, 기획부터 배포까지 전체를 맡겨도 됩니다. 세 가지 서비스의 범위를 확인해 보세요."
      />

      <section className="py-14 sm:py-20">
        <Container>
          <Reveal from="left">
            <div
              role="tablist"
              aria-label="서비스 종류"
              className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-2 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {SERVICES.map((service) => {
                const selected = service.id === activeId;
                const Icon = SERVICE_ICONS[service.id];
                return (
                  <button
                    key={service.id}
                    type="button"
                    role="tab"
                    id={`tab-${service.id}`}
                    aria-selected={selected}
                    aria-controls={`panel-${service.id}`}
                    onClick={() => setActiveId(service.id)}
                    className={`relative inline-flex h-11 shrink-0 items-center gap-2 rounded-full px-4 text-[14px] font-semibold transition-colors duration-200 sm:h-12 sm:px-5 sm:text-[15px] ${
                      selected ? 'text-white' : 'text-ink-soft hover:text-ink'
                    }`}
                  >
                    {selected ? (
                      <motion.span
                        layoutId="service-tab"
                        className="bg-brand-gradient absolute inset-0 rounded-full shadow-accent"
                        transition={reduce ? { duration: 0 } : { type: 'spring', stiffness: 420, damping: 36 }}
                      />
                    ) : (
                      <span className="absolute inset-0 rounded-full border border-line-strong bg-surface" aria-hidden="true" />
                    )}
                    <span className="relative inline-flex items-center gap-2">
                      <Icon size={18} weight="bold" className="hidden sm:inline" />
                      {service.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </Reveal>

          <div className="mt-10 min-h-[520px]">
            <motion.div
              key={active.id}
              id={`panel-${active.id}`}
              role="tabpanel"
              aria-labelledby={`tab-${active.id}`}
              initial={reduce ? false : 'hidden'}
              animate="visible"
              variants={staggerParent(0.1)}
              className="grid gap-10 lg:grid-cols-12 lg:gap-14"
            >
              <motion.div variants={staggerChild(0, -40)} className="lg:col-span-6">
                <IconBadge icon={ActiveIcon} />
                <h2 className="mt-6 text-[1.75rem] font-bold leading-[1.25] tracking-[-0.02em] sm:text-4xl">
                  {active.title}
                </h2>
                <p className="mt-4 text-base leading-[1.8] text-ink-soft sm:text-lg">{active.description}</p>

                <h3 className="mt-10 text-[13px] font-semibold text-ink-muted">이런 분께 맞습니다</h3>
                <ul className="mt-4 space-y-3">
                  {active.audience.map((line) => (
                    <li key={line} className="flex items-start gap-3 text-[15px] leading-[1.7] text-ink sm:text-base">
                      <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent">
                        <Check size={12} weight="bold" />
                      </span>
                      {line}
                    </li>
                  ))}
                </ul>

                <h3 className="mt-10 text-[13px] font-semibold text-ink-muted">포함되는 작업</h3>
                <div className="mt-4 flex flex-wrap gap-2">
                  {active.includes.map((item) => (
                    <Chip key={item} tone="accent">
                      {item}
                    </Chip>
                  ))}
                </div>
              </motion.div>

              <motion.div
                variants={{
                  hidden: { opacity: 0, x: 48 },
                  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: EASE } }
                }}
                className="lg:col-span-6"
              >
                <ServiceVisual service={active} />
              </motion.div>
            </motion.div>
          </div>
        </Container>
      </section>

      <section className="bg-night-glow relative overflow-hidden bg-night py-20 text-white sm:py-28">
        <Container className="relative">
          <Reveal from="left" className="max-w-2xl">
            <h2 className="text-balance text-[1.85rem] font-bold leading-[1.2] tracking-[-0.025em] sm:text-4xl lg:text-[2.75rem]">
              어떤 서비스든 함께 챙기는 것들
            </h2>
            <p className="mt-4 text-base leading-[1.75] text-white/70 sm:text-lg">
              만들고 끝이 아니라, 실제 주소로 열리고 문의가 도착할 때까지 진행합니다.
            </p>
          </Reveal>

          <motion.ul
            className="mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-5 lg:gap-6"
            variants={staggerParent(0.08)}
            initial={reduce ? false : 'hidden'}
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {EXTRAS.map((extra) => (
              <motion.li key={extra.title} variants={staggerChild(0, -32)} className="border-t border-white/15 pt-5">
                <extra.icon size={26} weight="duotone" className="text-sky" aria-hidden="true" />
                <h3 className="mt-4 text-base font-bold tracking-tight">{extra.title}</h3>
                <p className="mt-1.5 text-[14px] leading-[1.7] text-white/65">{extra.description}</p>
              </motion.li>
            ))}
          </motion.ul>
        </Container>
      </section>

      <CtaBand />
    </>
  );
}

export default ServicesPage;
